import {
  AlertOctagon,
  CheckCircle2,
  Crosshair,
  DoorClosed,
  Link2Off,
  LockKeyhole,
  MapPinned,
  Radio,
  Route,
  Shield,
  ShieldAlert,
  ShieldCheck,
  TimerReset,
  UsersRound,
} from "lucide-react";
import { useEffect, useState } from "react";

import { formatDurationRange } from "../lib/format";
import type { DefenceLineCompartment, GameCommand, GameState } from "../types";

const tactics = [
  {
    key: "hold",
    name: "Hold the entrance",
    description:
      "Maximum firepower at the outer barricade. High ammunition use; lowest chance of interior damage.",
    icon: Shield,
  },
  {
    key: "controlled_fallback",
    name: "Controlled fallback",
    description:
      "Yield damaged rooms and fight from prepared positions. Preserves people at the cost of infrastructure.",
    icon: DoorClosed,
  },
  {
    key: "conserve",
    name: "Conserve ammunition",
    description:
      "Fire only at close range and accept greater pressure on the door. Dangerous, but supplies last longer.",
    icon: Crosshair,
  },
] as const;

const lineRoleCopy: Record<DefenceLineCompartment["line_role"], string> = {
  outer: "Outer line",
  fortified: "Fortified fallback",
  fallback: "Fallback compartment",
  compromised: "Compromised route",
};

function roomLabel(roomType: string | null) {
  if (!roomType) return "Cleared chamber";
  return roomType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function legacyDefenceLine(state: GameState): DefenceLineCompartment[] {
  const roomSpaces = state.site.spaces.filter((space) => Boolean(space.room));
  return roomSpaces
    .sort((left, right) => {
      const leftEntrance = left.room?.type === "entrance" ? 0 : 1;
      const rightEntrance = right.room?.type === "entrance" ? 0 : 1;
      return leftEntrance - rightEntrance || left.id.localeCompare(right.id);
    })
    .map((space, index) => {
      const room = space.room!;
      const defensive = ["entrance", "armoury", "security_post"].includes(room.type);
      return {
        space_id: space.id,
        depth: index,
        room_type: room.type,
        room_status: room.status,
        condition: room.condition,
        defence_active: defensive && room.status === "operational",
        line_role:
          room.type === "entrance"
            ? "outer"
            : room.status !== "operational"
              ? "compromised"
              : defensive
                ? "fortified"
                : "fallback",
      };
    });
}

function StandingOrderSlider({
  label,
  description,
  value,
  min,
  max,
  step,
  busy,
  onCommit,
}: {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step: number;
  busy: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <label className="order-control">
      <span>
        <strong>{label}</strong>
        <output>{draft}%</output>
      </span>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={draft}
        disabled={busy}
        onChange={(event) => setDraft(Number(event.currentTarget.value))}
        onBlur={commit}
        onPointerUp={commit}
        onKeyUp={(event) => {
          if (event.key.startsWith("Arrow") || event.key === "Home" || event.key === "End") {
            commit();
          }
        }}
      />
      <small>{description}</small>
    </label>
  );
}

interface DefencePanelProps {
  state: GameState;
  busy: boolean;
  onCommand: (command: GameCommand) => Promise<unknown>;
}

export function DefencePanel({ state, busy, onCommand }: DefencePanelProps) {
  const threat = state.threat;
  const entrance = state.site.spaces.find(
    (space) => space.room?.type === "entrance",
  );
  const lineCompartments = state.defence_line?.compartments ?? legacyDefenceLine(state);
  const connectedFallbacks = state.defence_line?.fallback_space_ids.length
    ?? lineCompartments.filter(
      (compartment) =>
        compartment.line_role !== "outer"
        && compartment.room_status === "operational",
    ).length;
  const disconnectedDefensiveSpaces =
    state.defence_line?.disconnected_defensive_space_ids ?? [];
  const defenders = state.survivors.filter(
    (item) => item.alive && item.status === "home",
  );
  const living = state.survivors.filter((item) => item.alive);
  const continuityReady = state.research?.completed?.includes("continuity_protocols") ?? false;
  const siteAttacks = state.progress?.site_attacks_survived ?? 0;
  const relocationAttackRequirement =
    state.progress?.relocation_site_attack_requirement ?? 3;
  const activeExpedition = (state.expeditions ?? []).some(
    (expedition) => !["complete", "lost"].includes(expedition.phase),
  );
  const everyoneHome = !living.some((survivor) => survivor.status !== "home");
  const threatTooClose = Boolean(
    threat && threat.eta_range_seconds[0] <= 300,
  );
  const relocationRequirements = [
    {
      id: "continuity",
      name: "Continuity plan",
      met: continuityReady,
      metCopy: "Continuity protocols researched",
      blockedCopy: "Complete Continuity protocols",
    },
    {
      id: "site-record",
      name: "Proven shelter",
      met: siteAttacks >= relocationAttackRequirement,
      metCopy: `${siteAttacks}/${relocationAttackRequirement} required attacks survived at this site`,
      blockedCopy: `Survive ${relocationAttackRequirement - siteAttacks} more attack${relocationAttackRequirement - siteAttacks === 1 ? "" : "s"} at this site`,
    },
    {
      id: "survivors",
      name: "Community assembled",
      met: everyoneHome,
      metCopy: "Every living survivor is home",
      blockedCopy: "Bring every living survivor home",
    },
    {
      id: "expeditions",
      name: "Scavenging parties",
      met: !activeExpedition,
      metCopy: "No expedition is underway",
      blockedCopy: "Wait for all expeditions",
    },
    {
      id: "crisis",
      name: "Current defence",
      met: !state.active_crisis,
      metCopy: "No attack is in progress",
      blockedCopy: "Resolve the active attack",
    },
    {
      id: "warning",
      name: "Departure window",
      met: Boolean(threat) && !threatTooClose,
      metCopy: "A known attack is more than five minutes away",
      blockedCopy: threat
        ? "The reported attack is too close"
        : "Wait for a known threat window",
    },
  ];
  const relocationBlocks = relocationRequirements.filter(
    (requirement) => !requirement.met,
  );
  const relocationCandidates = state.survivors
    .filter((survivor) => survivor.alive && survivor.status === "home" && !survivor.assignment)
    .sort((left, right) => (right.skills.logistics ?? 0) - (left.skills.logistics ?? 0) || left.id.localeCompare(right.id));
  const [relocationWorkers, setRelocationWorkers] = useState<string[]>([]);
  const relocationTask = (state.tasks ?? []).find((task) => task.kind === "relocation");
  useEffect(() => {
    setRelocationWorkers((current) => {
      const valid = current.filter((id) => relocationCandidates.some((survivor) => survivor.id === id)).slice(0, 4);
      return valid.length ? valid : relocationCandidates.slice(0, 1).map((survivor) => survivor.id);
    });
  }, [state.survivors]);
  const setOrders = (orders: Partial<GameState["standing_orders"]>) => {
    void onCommand({ type: "set_standing_orders", orders });
  };

  return (
    <section className="defence-layout" aria-busy={busy}>
      <article className="threat-board panel-surface">
        <div className="threat-radar" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <span />
        </div>
        <header>
          <span className="eyebrow">Watch report</span>
          <h2>{threat ? "Movement beyond the ridge" : "No active warning"}</h2>
        </header>
        {threat ? (
          <>
            <div className="threat-estimate">
              <div>
                <AlertOctagon />
                <span>
                  <small>Estimated horde</small>
                  <strong>
                    {threat.observation.strength_range?.join("–") ?? "Unknown"}
                  </strong>
                </span>
              </div>
              <div>
                <TimerReset />
                <span>
                  <small>Estimated contact</small>
                  <strong>{formatDurationRange(threat.eta_range_seconds)}</strong>
                </span>
              </div>
              <div>
                <Radio />
                <span>
                  <small>Report confidence</small>
                  <strong>{threat.observation.confidence}%</strong>
                </span>
              </div>
            </div>
            <p className="intel-source">
              Source: {threat.observation.source}. This estimate may be stale.
            </p>
            {threat.composition && threat.composition.names.length > 0 && (
              <section className="horde-composition" aria-label="Observed horde composition">
                <header><AlertOctagon aria-hidden="true" /><span>Observed composition</span></header>
                <div>
                  {threat.composition.names.map((name, index) => (
                    <article className={`infected-${threat.composition?.keys[index] ?? "unknown"}`} key={`${name}-${index}`}>
                      <i aria-hidden="true"><b /></i>
                      <span><strong>{name}</strong><small>{threat.composition?.signs[index] ?? "Field signs remain uncertain."}</small></span>
                    </article>
                  ))}
                </div>
                <p>Composition changes casualties and structural pressure; strength alone is not the whole threat.</p>
              </section>
            )}
          </>
        ) : (
          <p>No credible movement has been reported near the bunker.</p>
        )}
        <section
          className="defence-topology"
          aria-labelledby="defence-line-title"
        >
          <header className="defence-topology-header">
            <span>
              <small className="eyebrow">Connected fallback plan</small>
              <h3 id="defence-line-title">Entrance to final position</h3>
            </span>
            <strong>
              <Route aria-hidden="true" />
              {lineCompartments.length} connected
            </strong>
          </header>
          <ol
            className="defence-line-track"
            aria-label="Connected defence compartments in breach order"
          >
            {lineCompartments.map((compartment) => {
              const label = roomLabel(compartment.room_type);
              const defensiveRoom = ["entrance", "armoury", "security_post"].includes(
                compartment.room_type ?? "",
              );
              const role = defensiveRoom && !compartment.defence_active
                ? "Defence offline"
                : lineRoleCopy[compartment.line_role];
              return (
                <li
                  className={`defence-line-node line-${compartment.line_role}`}
                  key={compartment.space_id}
                  aria-label={`${label}, ${role}, breach depth ${compartment.depth}`}
                >
                  <span className="defence-line-depth">
                    {compartment.depth === 0
                      ? "Surface gate"
                      : `Depth ${compartment.depth}`}
                  </span>
                  <span className="defence-line-room">
                    {compartment.line_role === "fortified" ? (
                      <ShieldCheck aria-hidden="true" />
                    ) : (
                      <DoorClosed aria-hidden="true" />
                    )}
                    <strong>{label}</strong>
                  </span>
                  <small>{role}</small>
                  {compartment.position_capacity != null && (
                    <small className="defence-position-staffing">{compartment.staffed_count ?? compartment.positioned_survivor_ids?.length ?? 0}/{compartment.position_capacity} positions staffed</small>
                  )}
                  {compartment.condition !== null && (
                    <span
                      className="defence-line-condition"
                      aria-label={`${compartment.condition}% integrity`}
                    >
                      <i>
                        <b style={{ width: `${compartment.condition}%` }} />
                      </i>
                      {compartment.condition}%
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          <p className="defence-line-guidance">
            A breach advances left to right. Controlled fallback yields these
            connected compartments in order; sealed chambers cannot act as a
            shortcut.
          </p>
          {disconnectedDefensiveSpaces.length > 0 && (
            <p className="defence-line-warning" role="status">
              <Link2Off aria-hidden="true" />
              {disconnectedDefensiveSpaces.length} operational defensive room
              {disconnectedDefensiveSpaces.length === 1 ? " is" : "s are"}{" "}
              cut off and contributes nothing until a route is open.
            </p>
          )}
        </section>
        <div className="readiness-grid">
          <div>
            <span>Entrance integrity</span>
            <strong>{entrance?.room?.condition ?? 0}%</strong>
            <i>
              <b style={{ width: `${entrance?.room?.condition ?? 0}%` }} />
            </i>
          </div>
          <div>
            <span>Available defenders</span>
            <strong>{state.defence_readiness?.active_defender_ids.length ?? defenders.length}</strong>
            <small>
              {state.defence_readiness
                ? `${state.defence_readiness.position_capacity} prepared positions`
                : `${defenders.reduce((total, item) => total + item.skills.combat, 0)} combined combat skill`}
            </small>
          </div>
          <div>
            <span>Connected fallback rooms</span>
            <strong>{connectedFallbacks}</strong>
            <small>operational positions beyond the entrance</small>
          </div>
          {state.defence_readiness && (
            <div>
              <span>Protected population</span>
              <strong>{state.defence_readiness.sheltered_count}</strong>
              <small>{state.defence_readiness.helper_ids.length} emergency helpers outside prepared positions</small>
            </div>
          )}
        </div>
      </article>
      <aside className="standing-orders panel-surface">
        <div className="inspector-kicker">
          <ShieldAlert size={14} />
          Standing orders
        </div>
        <h2>If nobody is here to command</h2>
        <p>
          The bunker follows this plan during an automatic defence. You can
          change it at any time before contact.
        </p>
        <div
          className="tactic-list"
          role="group"
          aria-label="Standing defence tactic"
        >
          {tactics.map(({ key, name, description, icon: Icon }) => (
            <button
              type="button"
              key={key}
              aria-pressed={state.standing_orders.tactic === key}
              className={state.standing_orders.tactic === key ? "selected" : ""}
              disabled={busy}
              onClick={() => setOrders({ tactic: key })}
            >
              <Icon size={19} />
              <span>
                <strong>{name}</strong>
                <small>{description}</small>
              </span>
              {state.standing_orders.tactic === key && <em>Active</em>}
            </button>
          ))}
        </div>
        <fieldset className="order-controls">
          <legend>Automatic thresholds</legend>
          <StandingOrderSlider
            label="Ammunition reserve"
            description="Keep this share of current ammunition stock untouched unless the bunker would otherwise fall."
            value={state.standing_orders.ammo_reserve_percent}
            min={0}
            max={75}
            step={5}
            busy={busy}
            onCommit={(value) => setOrders({ ammo_reserve_percent: value })}
          />
          <StandingOrderSlider
            label="Fallback condition"
            description="Yield a defensive room when its integrity reaches this level."
            value={state.standing_orders.fallback_condition}
            min={10}
            max={80}
            step={5}
            busy={busy}
            onCommit={(value) => setOrders({ fallback_condition: value })}
          />
          <StandingOrderSlider
            label="Evacuate below defence ratio"
            description="Abandon the final position when effective defence falls below this share of the attack."
            value={Math.round(
              state.standing_orders.evacuate_at_strength_ratio * 100,
            )}
            min={10}
            max={90}
            step={5}
            busy={busy}
            onCommit={(value) =>
              setOrders({ evacuate_at_strength_ratio: value / 100 })
            }
          />
        </fieldset>
        <section
          className="planned-relocation"
          aria-labelledby="planned-relocation-title"
        >
          <header>
            <div className="planned-relocation-title">
              <MapPinned aria-hidden="true" />
              <span>
                <small className="eyebrow">Continuity option</small>
                <h3 id="planned-relocation-title">Planned relocation</h3>
              </span>
            </div>
            <strong
              className={`relocation-status ${relocationBlocks.length > 0 ? "blocked" : "ready"}`}
            >
              {relocationBlocks.length > 0
                ? `${relocationBlocks.length} blocker${relocationBlocks.length === 1 ? "" : "s"}`
                : "Departure ready"}
            </strong>
          </header>
          <p>
            Leave a proven bunker on the community’s terms. Rooms and unfinished
            work are lost, but the departure is prepared, survivors travel
            together, and this site can earn a permanent legacy mark.
          </p>
          <div className="relocation-record">
            <span>
              <small>Attacks survived here</small>
              <strong>{Math.min(relocationAttackRequirement, siteAttacks)}/{relocationAttackRequirement}</strong>
            </span>
            <progress
              max={relocationAttackRequirement}
              value={Math.min(relocationAttackRequirement, siteAttacks)}
              aria-label={`${Math.min(relocationAttackRequirement, siteAttacks)} of ${relocationAttackRequirement} required attacks survived at this site`}
            />
          </div>
          <div className="relocation-requirements">
            <h4>Departure checklist</h4>
            <ul aria-label="Planned relocation requirements">
              {relocationRequirements.map((requirement) => (
                <li
                  className={requirement.met ? "met" : "missing"}
                  key={requirement.id}
                >
                  {requirement.met ? (
                    <CheckCircle2 aria-hidden="true" />
                  ) : (
                    <LockKeyhole aria-hidden="true" />
                  )}
                  <span>
                    <strong>{requirement.name}</strong>
                    <small>
                      {requirement.met
                        ? requirement.metCopy
                        : requirement.blockedCopy}
                    </small>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {relocationTask ? (
            <div className="relocation-work-order" role="status">
              <TimerReset aria-hidden="true" />
              <span><small>Community packing underway</small><strong>{relocationTask.seconds_left == null ? "Paused" : `${Math.ceil(relocationTask.seconds_left / 60)}m remaining`}</strong></span>
              <button type="button" disabled={busy} onClick={() => void onCommand({ type: "cancel_task", task_id: relocationTask.id })}>Cancel</button>
            </div>
          ) : (
            <>
              <fieldset className="relocation-worker-picker">
                <legend><UsersRound aria-hidden="true" />Packing team · {relocationWorkers.length}/4</legend>
                {relocationCandidates.map((survivor) => {
                  const selected = relocationWorkers.includes(survivor.id);
                  return (
                    <label className={selected ? "selected" : ""} key={survivor.id}>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!selected && relocationWorkers.length >= 4}
                        onChange={() => setRelocationWorkers(selected
                          ? relocationWorkers.filter((id) => id !== survivor.id)
                          : [...relocationWorkers, survivor.id])}
                      />
                      <span><strong>{survivor.name}</strong><small>{survivor.skills.logistics ?? 0}/5 logistics</small></span>
                    </label>
                  );
                })}
              </fieldset>
              <button
                type="button"
                className="secondary-button"
                disabled={busy || relocationBlocks.length > 0 || relocationWorkers.length === 0}
                onClick={() => void onCommand({ type: "prepare_relocation", worker_ids: relocationWorkers })}
              >
                <MapPinned aria-hidden="true" />Prepare relocation
              </button>
            </>
          )}
        </section>
      </aside>
    </section>
  );
}
